import React, { useState, useEffect, useRef } from 'react';
import { Button, Flex, Input, Text, Card } from './base-components';
import { useCrowdVoltSendOTP, useCrowdVoltVerifyOTP, useCrowdVoltConnectSession } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { trackEvent } from '../services/analytics';

type FlowStep = 'PHONE' | 'CODE';

interface Props {
  open: boolean;
  onClose: () => void;
}

type ExtensionAuthPayload = {
  session_token: string;
  session_jwt?: string;
  user_id?: string;
  email?: string;
  phone?: string;
  score?: string;
};

const normalizeExtensionAuthPayload = (raw: any): ExtensionAuthPayload | null => {
  if (!raw || typeof raw !== 'object') return null;

  const data = raw.payload || raw.data || raw;
  const sessionToken = String(data?.session_token || data?.sessionToken || '').trim();
  if (!sessionToken) return null;

  const payload: ExtensionAuthPayload = { session_token: sessionToken };
  const sessionJwt = String(data?.session_jwt || data?.sessionJwt || '').trim();
  const userId = String(data?.user_id || data?.userId || '').trim();
  const email = String(data?.email || '').trim();
  const phone = String(data?.phone || data?.phone_number || '').trim();
  const score = String(data?.score || data?.captcha_token || '').trim();

  if (sessionJwt) payload.session_jwt = sessionJwt;
  if (userId) payload.user_id = userId;
  if (email) payload.email = email;
  if (phone) payload.phone = phone;
  if (score) payload.score = score;
  return payload;
};

export const ConnectCrowdVoltModal: React.FC<Props> = ({ open, onClose }) => {
  const { showSuccess } = useToast();
  const sendOTPMutation = useCrowdVoltSendOTP();
  const verifyOTPMutation = useCrowdVoltVerifyOTP();
  const connectSessionMutation = useCrowdVoltConnectSession();

  const [step, setStep] = useState<FlowStep>('PHONE');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [extensionJson, setExtensionJson] = useState('');
  const [waitingForExtension, setWaitingForExtension] = useState(false);
  const [showOtpFallback, setShowOtpFallback] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const didAutoConnectRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setStep('PHONE');
      setPhone('');
      setCode('');
      setError('');
      setExtensionJson('');
      setWaitingForExtension(false);
      setShowOtpFallback(false);
      didAutoConnectRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (step === 'CODE') {
      setTimeout(() => codeInputRef.current?.focus(), 100);
    }
  }, [step]);

  useEffect(() => {
    if (!open) return;

    const handleExtensionPayload = async (raw: any) => {
      if (didAutoConnectRef.current) return;
      const payload = normalizeExtensionAuthPayload(raw);
      if (!payload) return;
      didAutoConnectRef.current = true;
      try {
        setError('');
        setWaitingForExtension(false);
        await connectSessionMutation.mutateAsync(payload);
        showSuccess('CrowdVolt connected ✓');
        trackEvent('crowdvolt_connected', { method: 'extension' });
        onClose();
      } catch (err: any) {
        didAutoConnectRef.current = false;
        setError(err?.message || 'Failed to connect extension session');
      }
    };

    const messageHandler = (event: MessageEvent) => {
      const data = event?.data;
      if (!data) return;
      if (data.type === 'CROWDVOLT_AUTH' || data.type === 'crowdvolt-auth' || data.session_token) {
        handleExtensionPayload(data);
      }
    };

    const customEventHandler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      handleExtensionPayload(detail);
    };

    window.addEventListener('message', messageHandler);
    window.addEventListener('crowdvolt-auth', customEventHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
      window.removeEventListener('crowdvolt-auth', customEventHandler);
    };
  }, [open, connectSessionMutation, showSuccess, onClose]);

  if (!open) return null;

  const handleSendOTP = async () => {
    setError('');
    if (!phone) {
      setError('Please enter your phone number');
      return;
    }
    try {
      await sendOTPMutation.mutateAsync(phone);
      setStep('CODE');
    } catch (err: any) {
      setError(err?.message || 'Failed to send code, try again');
    }
  };

  const handleVerifyOTP = async (codeValue?: string) => {
    const finalCode = codeValue ?? code;
    setError('');
    if (finalCode.length < 6) return;
    try {
      await verifyOTPMutation.mutateAsync(finalCode);
      showSuccess('CrowdVolt connected ✓');
      trackEvent('crowdvolt_connected', { method: 'otp' });
      onClose();
    } catch (err: any) {
      const status = (err as any)?.status;
      if (status === 404) {
        // Session expired — need to restart
        setError('__expired__');
      } else {
        setError(err?.message || 'Verification failed');
      }
    }
  };

  const handleCodeChange = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setError('');
    if (digits.length === 6) {
      handleVerifyOTP(digits);
    }
  };

  const handleConnectFromJson = async () => {
    setError('');
    let parsed: any;
    try {
      parsed = JSON.parse(extensionJson);
    } catch (_e) {
      setError('Invalid JSON. Paste the extension auth payload.');
      return;
    }
    const payload = normalizeExtensionAuthPayload(parsed);
    if (!payload) {
      setError('Missing session_token in extension payload.');
      return;
    }
    try {
      await connectSessionMutation.mutateAsync(payload);
      showSuccess('CrowdVolt connected ✓');
      trackEvent('crowdvolt_connected', { method: 'json_paste' });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to connect extension session');
    }
  };

  const handleStartExtensionFlow = () => {
    setError('');
    setWaitingForExtension(true);
    window.postMessage({ type: 'CROWDVOLT_CONNECT_START' }, '*');
    window.open('https://www.crowdvolt.com', '_blank', 'noopener,noreferrer');
  };

  const isPending =
    sendOTPMutation.isPending ||
    verifyOTPMutation.isPending ||
    connectSessionMutation.isPending;

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      animate="fadeIn"
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="sm"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex align="center" gap={3} mb={4}>
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <span className="text-violet-400 text-lg font-bold">CV</span>
          </div>
          <div>
            <Text variant="heading">Connect CrowdVolt</Text>
            <Text variant="muted" size="sm">
              Secure sign-in through CrowdVolt
            </Text>
          </div>
        </Flex>

        <div className="mb-4 rounded-lg border border-white/[0.08] bg-white/[0.03] p-3">
          <Text variant="title" size="sm">Recommended: One-click CrowdVolt login</Text>
          <Text variant="muted" size="xs" mt={1}>
            Click continue, log in on CrowdVolt, then you will be connected automatically.
          </Text>
          <Flex gap={2} mt={3}>
            <Button
              variant="primary"
              onClick={handleStartExtensionFlow}
              disabled={isPending}
            >
              {waitingForExtension ? 'Waiting for login…' : 'Continue with CrowdVolt'}
            </Button>
          </Flex>
          {waitingForExtension && (
            <Text variant="muted" size="xs" mt={2}>
              After login on CrowdVolt, return to this tab. We will auto-connect.
            </Text>
          )}
          <Text variant="muted" size="xs" mt={3}>
            Fallback: paste extension JSON payload.
          </Text>
          <textarea
            value={extensionJson}
            onChange={(e) => { setExtensionJson(e.target.value); setError(''); }}
            placeholder='{"session_token":"...","session_jwt":"...","user_id":"..."}'
            className="mt-2 h-20 w-full rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#3b82f6]/50"
            disabled={isPending}
          />
          <Flex justify="end" mt={2}>
            <Button variant="primary" onClick={handleConnectFromJson} disabled={isPending || !extensionJson.trim()}>
              {connectSessionMutation.isPending ? 'Connecting…' : 'Connect Session'}
            </Button>
          </Flex>
        </div>

        <div className="mb-4 border-t border-white/[0.08] pt-3">
          <button
            type="button"
            className="text-xs text-white/70 hover:text-white transition-colors"
            onClick={() => setShowOtpFallback((v) => !v)}
            disabled={isPending}
          >
            {showOtpFallback ? 'Hide SMS fallback' : 'Use SMS fallback (advanced)'}
          </button>
        </div>

        {showOtpFallback && step === 'PHONE' && (
          <>
            <Flex direction="col" mb={4}>
              <Input
                type="tel"
                label="Phone Number"
                placeholder="+1 (555) 123-4567"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
              />
              <Text variant="muted" size="xs" mt={1}>
                Enter in E.164 format, e.g. +13146034744
              </Text>
            </Flex>

            {error && (
              <Text variant="error" mb={3}>{error}</Text>
            )}

            <Flex justify="end" gap={2}>
              <Button variant="secondary" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSendOTP} disabled={isPending}>
                {sendOTPMutation.isPending ? 'Sending…' : 'Send Code'}
              </Button>
            </Flex>
          </>
        )}

        {showOtpFallback && step === 'CODE' && (
          <>
            {error === '__expired__' ? (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <Text variant="error" size="sm">Code expired, please restart</Text>
              </div>
            ) : error ? (
              <Text variant="error" mb={3}>{error}</Text>
            ) : null}

            {error !== '__expired__' && (
              <Flex direction="col" mb={4}>
                <label className="block text-sm text-white/60 mb-1">6-Digit Code</label>
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-3 text-2xl text-center font-mono text-white tracking-[0.5em] placeholder-white/20 focus:outline-none focus:border-[#3b82f6]/50"
                  placeholder="——————"
                />
                <Text variant="muted" size="xs" mt={1}>
                  Auto-submits on the 6th digit
                </Text>
              </Flex>
            )}

            <Flex justify="between" gap={2}>
              <Button
                variant="secondary"
                onClick={() => { setStep('PHONE'); setCode(''); setError(''); }}
                disabled={isPending}
              >
                ← Back
              </Button>
              {error !== '__expired__' && (
                <Button
                  variant="primary"
                  onClick={() => handleVerifyOTP()}
                  disabled={isPending || code.length < 6}
                >
                  {verifyOTPMutation.isPending ? 'Verifying…' : 'Verify'}
                </Button>
              )}
            </Flex>
          </>
        )}
      </Card>
    </Flex>
  );
};
